// ── BCHNostr — Business profiles (kind 30499) ─────────────────────
//
// A business profile is an addressable event authored by the business's
// own key (the account IS the business). BCHNostr owns this format; any
// tool (e.g. the BCHPay PWA) may conform to it. See NIP.md § Kind 30499.
//
// This module is the framework-free layer: the kind/tag constants,
// categories, and pure build/parse helpers. UI reads and writes through
// these so the on-wire shape lives in exactly one place.

import type { Event } from 'nostr-tools';

export const BUSINESS_KIND = 30499;
/** Fixed d-tag for a pubkey's primary business profile. */
export const BUSINESS_D_TAG = 'bchnostr/business';
/** Relay-indexable discovery tag every business profile carries. */
export const BUSINESS_TAG = 'bch-business';
/** Prefix for the indexable category tag (mirrors BCH Live's bch-cat-). */
export const BUSINESS_CAT_PREFIX = 'bch-biz-cat-';
/** Prefix for the indexable professional-type tag. */
export const BUSINESS_TYPE_PREFIX = 'bch-pro-';

// ── Professional type ─────────────────────────────────────────────
//
// The Professional Profile toggle covers more than shopfronts. The TYPE
// labels the professional and rides on an indexed `t` tag so discovery can
// filter on it server-side. (Podcasts are NOT a professional type — they
// are a fully standalone feature; see lib/podcast.ts.)

export type ProfessionalType =
  | 'business' | 'creator' | 'organization' | 'developer' | 'other';

export const PROFESSIONAL_TYPES: Array<{ id: ProfessionalType; label: string; hint: string }> = [
  { id: 'business',     label: 'Business',     hint: 'A shop, cafe, or service that accepts BCH' },
  { id: 'creator',      label: 'Creator',      hint: 'An artist, writer, streamer, or musician' },
  { id: 'organization', label: 'Organization', hint: 'A non-profit, DAO, or community group' },
  { id: 'developer',    label: 'Developer',    hint: 'An open-source project or dev-for-hire' },
  { id: 'other',        label: 'Other',        hint: 'Something else entirely' },
];

const PROFESSIONAL_TYPE_IDS = new Set<string>(PROFESSIONAL_TYPES.map(t => t.id));

/** Narrow an untrusted string to a known ProfessionalType, defaulting to `business`. */
export function normalizeProfessionalType(raw: string | undefined | null): ProfessionalType {
  const v = (raw || '').trim().toLowerCase();
  return PROFESSIONAL_TYPE_IDS.has(v) ? (v as ProfessionalType) : 'business';
}

export function professionalTypeLabel(id: string | undefined): string {
  return PROFESSIONAL_TYPES.find(t => t.id === normalizeProfessionalType(id))?.label || 'Business';
}

/** Canonical category list. `id` rides on the tag; `label` is shown. */
export const BUSINESS_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'food', label: 'Food & Drink' },
  { id: 'retail', label: 'Retail' },
  { id: 'services', label: 'Services' },
  { id: 'online', label: 'Online' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'technology', label: 'Technology' },
  { id: 'other', label: 'Other' },
];

export function categoryLabel(id: string | undefined): string {
  return BUSINESS_CATEGORIES.find(c => c.id === id)?.label || 'Other';
}

/** Ordered days for the opening-hours editor / display. */
export const BUSINESS_DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// ── Shape ─────────────────────────────────────────────────────────

/** Rich, non-indexed fields carried in the event `content` JSON. */
export interface BusinessContent {
  /** Professional type — decides which extra surfaces unlock. */
  proType?: ProfessionalType;
  category?: string;
  tagline?: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  hours?: Record<string, string>;
  onlineOnly?: boolean;
  acceptsCashtokens?: boolean;
  socials?: Record<string, string>;
  gallery?: string[];
}

/** A parsed business profile, event + indexed tags + content merged. */
export interface BusinessProfile extends BusinessContent {
  /** Always populated (defaults to `business` for legacy profiles). */
  proType: ProfessionalType;
  pubkey: string;
  /** Display name from the `name` tag (falls back to kind 0 elsewhere). */
  name: string;
  /** Primary CashAddr receiving address, if present. */
  bch: string;
  /** Geohash for map / near-me, if present. */
  geohash: string;
  /** Human-readable location, if present. */
  location: string;
  /** Origin marker (e.g. "bchpay"); a CLAIM only — earns no badge alone. */
  source: string;
  /**
   * Self-asserted verified-business expiry (unix seconds), from the discovery
   * marker on the profile. A CONVENIENCE hint for merchant discovery — 0 when
   * absent. NOT a trust signal: confirm against the issuer-signed grant
   * (lib/businessVerify.ts) before rendering the Verified badge.
   */
  verifiedUntil: number;
  event: Event;
  created_at: number;
}

// ── Build ─────────────────────────────────────────────────────────

export interface BusinessDraft {
  name: string;
  category: string;
  /** Professional type. Defaults to `business` when omitted. */
  proType?: ProfessionalType;
  location?: string;
  bch?: string;
  geohash?: string;
  onlineOnly?: boolean;
  content?: BusinessContent;
  /**
   * When set, stamp the verified-business discovery marker onto the profile
   * (see lib/businessVerify.ts). This is a self-asserted CONVENIENCE hint for
   * merchant-discovery consumers (BCH Local); the authoritative signal is the
   * issuer-signed grant, which readers must still confirm. Value is the
   * verification expiry (unix seconds); omit/undefined to leave unverified.
   */
  verifiedUntil?: number;
}

/** The `t` marker + tag that a verified business profile carries. */
const VERIFIED_MARKER_TAG = 'bch-verified-business';

/**
 * Build the tags + content for a kind 30499 publish. The caller supplies
 * kind/created_at/signing; this owns the on-wire layout so the reader and
 * writer never drift.
 */
export function buildBusinessEvent(draft: BusinessDraft): {
  tags: string[][];
  content: string;
} {
  const proType = normalizeProfessionalType(draft.proType);

  const tags: string[][] = [
    ['d', BUSINESS_D_TAG],
    ['t', BUSINESS_TAG],
    ['t', `${BUSINESS_TYPE_PREFIX}${proType}`],
  ];

  if (draft.category) tags.push(['t', `${BUSINESS_CAT_PREFIX}${draft.category}`]);
  if (draft.name.trim()) tags.push(['name', draft.name.trim()]);
  if (draft.bch) tags.push(['bch', draft.bch]);
  if (draft.geohash) tags.push(['g', draft.geohash]);
  if (draft.location?.trim()) tags.push(['location', draft.location.trim()]);
  // Verified-business discovery marker. A relay-indexable `t` tag so a
  // merchant-discovery query can filter for verified businesses server-side,
  // plus a `verified` tag carrying the expiry. Self-asserted convenience hint
  // only — the authoritative signal is the issuer-signed grant.
  if (draft.verifiedUntil && draft.verifiedUntil > Math.floor(Date.now() / 1000)) {
    tags.push(['t', VERIFIED_MARKER_TAG]);
    tags.push(['verified', 'bchnostr', String(draft.verifiedUntil)]);
  }
  tags.push(['alt', 'A Bitcoin Cash professional profile on BCHNostr']);

  const content: BusinessContent = {
    proType,
    category: draft.category || undefined,
    onlineOnly: draft.onlineOnly || undefined,
    ...draft.content,
  };

  return { tags, content: JSON.stringify(content) };
}

/**
 * Tags + content for RETIRING a business profile (toggle off). Publishing
 * an empty replaceable event is how the rest of the app removes 30078
 * side-data; the same convention applies here. Discovery treats a profile
 * with no `bch-business` tag / empty content as absent.
 */
export function retireBusinessEvent(): { tags: string[][]; content: string } {
  return {
    tags: [
      ['d', BUSINESS_D_TAG],
      ['alt', 'Business profile removed'],
    ],
    content: '',
  };
}

// ── Parse ─────────────────────────────────────────────────────────

function tagValue(ev: Event, name: string): string {
  return ev.tags.find(t => t[0] === name)?.[1] || '';
}

/** Is this event a live (non-retired) business profile? */
export function isActiveBusiness(ev: Event | null | undefined): boolean {
  if (!ev || ev.kind !== BUSINESS_KIND) return false;
  const hasTag = ev.tags.some(t => t[0] === 't' && t[1] === BUSINESS_TAG);
  return hasTag && ev.content.trim() !== '';
}

/**
 * Parse a kind 30499 event into a BusinessProfile, or null if it isn't an
 * active business profile.
 */
export function parseBusiness(ev: Event): BusinessProfile | null {
  if (!isActiveBusiness(ev)) return null;

  let content: BusinessContent = {};
  try {
    content = JSON.parse(ev.content) as BusinessContent;
  } catch {
    content = {};
  }

  // Category from the indexed tag takes precedence over content.
  const catTag = ev.tags.find(t => t[0] === 't' && t[1].startsWith(BUSINESS_CAT_PREFIX));
  const category = catTag ? catTag[1].slice(BUSINESS_CAT_PREFIX.length) : content.category || '';

  // Same for the professional type. Legacy profiles have neither the tag nor
  // the content field, and normalize() maps them to 'business'.
  const typeTag = ev.tags.find(t => t[0] === 't' && t[1].startsWith(BUSINESS_TYPE_PREFIX));
  const proType = normalizeProfessionalType(
    typeTag ? typeTag[1].slice(BUSINESS_TYPE_PREFIX.length) : content.proType,
  );

  return {
    ...content,
    proType,
    category,
    pubkey: ev.pubkey,
    name: tagValue(ev, 'name'),
    bch: tagValue(ev, 'bch'),
    geohash: tagValue(ev, 'g'),
    location: tagValue(ev, 'location'),
    source: ev.tags.find(t => t[0] === 'source')?.[1] || '',
    verifiedUntil: (() => {
      const v = ev.tags.find(t => t[0] === 'verified' && t[1] === 'bchnostr')?.[2];
      const n = v ? parseInt(v, 10) : 0;
      return Number.isFinite(n) && n > 0 ? n : 0;
    })(),
    event: ev,
    created_at: ev.created_at,
  };
}
